import { Component } from 'react';
import { withTranslation } from 'react-i18next';

import ReactSelect from '../../../../components/ReactSelect';
import { GRID_SELECT } from '../../GscTools/selectStyles';
import { PRESET } from '../thresholds';

/**
 * Block 01 — the criteria the retrieved queries are reviewed against.
 *
 * It has its own Apply because it asks Search Console nothing: every criterion here is read over
 * rows already in memory, so moving one re-reads the queue rather than re-fetching it. The note
 * beside the button says so, so the page never implies that Google filtered anything.
 *
 * The editor is a draft until applied, like the scope bar above it, so the queue on screen always
 * belongs to criteria somebody chose rather than drifting as they typed.
 */
class Thresholds extends Component {
    constructor(props) {
        super(props);
        this.handleNumber = this.handleNumber.bind(this);
    }

    handleNumber(field, value) {
        // Kept as the raw string while typing: parsing here would turn a half-written "15" into a
        // 1 and move the field under the user's hands. thresholdErrors does the reading.
        this.props.onChange({ [field]: value });
    }

    render() {
        const { t, draft, errors, dirty, disabled, onChange, onApply } = this.props;
        const isLowCtr = draft.preset === PRESET.LOW_CTR;
        const presetOptions = Object.values(PRESET).map((preset) => ({
            value: preset,
            label: t(`tools.search_opportunities.presets.${preset}`),
        }));

        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-header d-flex justify-content-between">
                    <div>
                        <p className="card-title">
                            {t('tools.search_opportunities.criteria.title')}
                        </p>
                        <p className="gsc-tool-sub">
                            {t('tools.search_opportunities.criteria.description')}
                        </p>
                    </div>
                </div>

                <div className="card-body">
                    <div className="gsc-tool-criteria gsc-tool-criteria-bands">
                        <div className="gsc-tool-field">
                            <label>{t('tools.search_opportunities.criteria.preset')}</label>
                            <ReactSelect
                                options={presetOptions}
                                value={presetOptions.find((item) => item.value === draft.preset)}
                                blurInputOnSelect={true}
                                classname="gsc-tool-select"
                                controlStyles={GRID_SELECT}
                                customeOnChange={(item) => onChange({ preset: item.value })}
                            />
                            <span className="gsc-tool-field-note">
                                {t(`tools.search_opportunities.presets.${draft.preset}_note`)}
                            </span>
                        </div>

                        <div className="gsc-tool-field">
                            <label htmlFor="so-min-position">
                                {t('tools.search_opportunities.criteria.position')}
                            </label>
                            <div className="gsc-tool-criteria-pair">
                                <input
                                    id="so-min-position"
                                    type="number"
                                    min="1"
                                    className="form-control"
                                    value={draft.minPosition}
                                    onChange={(event) =>
                                        this.handleNumber('minPosition', event.target.value)
                                    }
                                />
                                <input
                                    type="number"
                                    min="1"
                                    className="form-control"
                                    aria-label={t(
                                        'tools.search_opportunities.criteria.max_position',
                                    )}
                                    value={draft.maxPosition}
                                    onChange={(event) =>
                                        this.handleNumber('maxPosition', event.target.value)
                                    }
                                />
                            </div>
                        </div>

                        <div className="gsc-tool-field">
                            <label htmlFor="so-min-impressions">
                                {t('tools.search_opportunities.criteria.impressions')}
                            </label>
                            <input
                                id="so-min-impressions"
                                type="number"
                                min="0"
                                className="form-control"
                                value={draft.minImpressions}
                                onChange={(event) =>
                                    this.handleNumber('minImpressions', event.target.value)
                                }
                            />
                        </div>

                        {isLowCtr && (
                            <div className="gsc-tool-field">
                                <label htmlFor="so-max-ctr">
                                    {t('tools.search_opportunities.criteria.ctr')}
                                </label>
                                <input
                                    id="so-max-ctr"
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    className="form-control"
                                    // Edited as a percentage and stored as the fraction Search
                                    // Console reports, so the ceiling and the CTR column are the
                                    // same quantity.
                                    value={
                                        draft.maxCtr === '' || draft.maxCtr === null
                                            ? ''
                                            : Number((draft.maxCtr * 100).toFixed(4))
                                    }
                                    onChange={(event) =>
                                        this.handleNumber(
                                            'maxCtr',
                                            event.target.value === ''
                                                ? ''
                                                : Number(event.target.value) / 100,
                                        )
                                    }
                                />
                                <span className="gsc-tool-field-note">
                                    {t('tools.search_opportunities.criteria.ctr_note')}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="gsc-tool-criteria-actions">
                        <span className="gsc-tool-field-note">
                            {t('tools.search_opportunities.criteria.local_note')}
                        </span>
                        <button
                            type="button"
                            className="button button-filled button-primary width-sm btn"
                            onClick={onApply}
                            disabled={disabled || !!errors.length || !dirty}
                        >
                            {t('tools.search_opportunities.criteria.apply')}
                        </button>
                    </div>

                    {errors.map((error) => (
                        <div key={error} className="gsc-tool-banner error">
                            {error}
                        </div>
                    ))}
                </div>
            </div>
        );
    }
}

export default withTranslation()(Thresholds);
